"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTemplateUrl = getTemplateUrl;
exports.getTemplateLatestVersion = getTemplateLatestVersion;
exports.getTemplateVSLatestVersion = getTemplateVSLatestVersion;
exports.getTemplateZipUrlByVersion = getTemplateZipUrlByVersion;
exports.fetchZipFromUrl = fetchZipFromUrl;
exports.unzip = unzip;
exports.renderTemplateFileData = renderTemplateFileData;
exports.renderTemplateFileName = renderTemplateFileName;
exports.getSampleInfoFromName = getSampleInfoFromName;
exports.zipFolder = zipFolder;
exports.downloadDirectory = downloadDirectory;
exports.getSampleFileInfo = getSampleFileInfo;
exports.runWithLimitedConcurrency = runWithLimitedConcurrency;
exports.convertToLangKey = convertToLangKey;
exports.convertToUrl = convertToUrl;
exports.simplifyAxiosError = simplifyAxiosError;
exports.isApiLimitError = isApiLimitError;
exports.setGeneralSensitivityLabel = setGeneralSensitivityLabel;
const tslib_1 = require("tslib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const adm_zip_1 = tslib_1.__importDefault(require("adm-zip"));
const axios_1 = tslib_1.__importDefault(require("axios"));
const fs = tslib_1.__importStar(require("fs-extra"));
const lodash_1 = require("lodash");
const mustache_1 = tslib_1.__importStar(require("mustache"));
const path_1 = tslib_1.__importDefault(require("path"));
const semver_1 = tslib_1.__importDefault(require("semver"));
const graphClient_1 = require("../../client/graphClient");
const constants_1 = require("../../common/constants");
const localizeUtils_1 = require("../../common/localizeUtils");
const requestUtils_1 = require("../../common/requestUtils");
const samples_1 = require("../../common/samples");
const templates_config_json_1 = tslib_1.__importDefault(require("../../common/templates-config.json"));
const error_1 = require("../../error");
const constants_2 = require("../../question/constants");
const CopilotGptManifestUtils_1 = require("../driver/teamsApp/utils/CopilotGptManifestUtils");
const constant_1 = require("./constant");
const packageJson = require("../../../package.json");
async function getTemplateUrl(programmingLanguage, getLatestVersion, platform) {
    return platform === teamsfx_api_1.Platform.VS
        ? await getTemplateVSUrl(programmingLanguage)
        : await getTemplateVSCUrl(programmingLanguage, getLatestVersion);
}
// Return undefined to use local templates.
async function getTemplateVSCUrl(programmingLanguage, getLatestVersion) {
    const templateVersionEnv = process.env["TEMPLATE_VERSION"];
    if (templateVersionEnv === "local") {
        return;
    }
    else if (templateVersionEnv) {
        return getTemplateZipUrlByVersion(programmingLanguage, templateVersionEnv, templates_config_json_1.default.tagPrefix);
    }
    const version = packageJson.version;
    if (version.includes("alpha")) {
        // daily build version
        return;
    }
    else if (version.includes("beta")) {
        // prerelease build version
    }
    else if (version.includes("rc")) {
        // rc version before stable / prerelease release
        return getTemplateZipUrlByVersion(programmingLanguage, "0.0.0-rc", templates_config_json_1.default.tagPrefix);
    }
    else {
        // stable version
    }
    const latestVersion = await getLatestVersion();
    if (semver_1.default.gt(latestVersion, templates_config_json_1.default.localVersion)) {
        // Upstream latest version is higher than the local version, return upstream templates url for downloading.
        return getTemplateZipUrlByVersion(programmingLanguage, latestVersion, templates_config_json_1.default.tagPrefix);
    }
}
async function getTemplateVSUrl(name) {
    // If the template is a daily version, return undefined to use local template.
    if (templates_config_json_1.default.useLocalTemplate) {
        return;
    }
    // Env override: explicit prerelease flag → use RC version.
    if (process.env.TEAMSFX_TEMPLATE_PRERELEASE === teamsfx_api_1.Platform.VS) {
        return getTemplateZipUrlByVersion(name, "0.0.0-rc", templates_config_json_1.default.vstagPrefix);
    }
    // VS stable release ships with a beta fx-core (unlike VSC which ships stable).
    // A beta fx-core therefore indicates a pre-release test build → use RC templates.
    const version = packageJson.version;
    if (version.includes("beta")) {
        return getTemplateZipUrlByVersion(name, "0.0.0-rc", templates_config_json_1.default.vstagPrefix);
    }
    // Stable fx-core means an actual VS stable release → look up the latest stable VS templates.
    const latestVsVersion = await getTemplateVSLatestVersion();
    return getTemplateZipUrlByVersion(name, latestVsVersion, templates_config_json_1.default.vstagPrefix);
}
async function selectTemplateVersion(getTags, tagPrefix = templates_config_json_1.default.tagPrefix, versionPattern = templates_config_json_1.default.version) {
    const versionList = (await getTags()).map((tag) => tag.replace(tagPrefix, ""));
    const selectedVersion = semver_1.default.maxSatisfying(versionList, versionPattern);
    return selectedVersion ?? undefined;
}
async function fetchTagList(url, tryLimits, timeoutInMs) {
    const res = await (0, requestUtils_1.sendRequestWithTimeout)(async (cancelToken) => {
        return await axios_1.default.get(url, {
            cancelToken: cancelToken,
        });
    }, timeoutInMs, tryLimits);
    return res.data;
}
async function getTemplateLatestVersion(tryLimits = constant_1.defaultTryLimits, timeoutInMs = constant_1.defaultTimeoutInMs) {
    const templateTagListURL = templates_config_json_1.default.tagListURL;
    const selectedVersion = await selectTemplateVersion(async () => (await fetchTagList(templateTagListURL, tryLimits, timeoutInMs)).replace(/\r/g, "").split("\n"));
    if (!selectedVersion) {
        throw new Error(`Failed to find valid template`);
    }
    return selectedVersion;
}
async function getTemplateVSLatestVersion(tryLimits = constant_1.defaultTryLimits, timeoutInMs = constant_1.defaultTimeoutInMs) {
    const selectedVersion = await selectTemplateVersion(async () => (await fetchTagList(templates_config_json_1.default.tagListURL, tryLimits, timeoutInMs))
        .replace(/\r/g, "")
        .split("\n"), templates_config_json_1.default.vstagPrefix, templates_config_json_1.default.vsVersionPattern);
    if (!selectedVersion) {
        throw new Error("Failed to find valid VS template version");
    }
    return selectedVersion;
}
function getTemplateZipUrlByVersion(name, version, prefex) {
    return `${templates_config_json_1.default.templateDownloadBaseURL}/${prefex}${version}/${name}${templates_config_json_1.default.templateExt}`;
}
async function fetchZipFromUrl(url, tryLimits = constant_1.defaultTryLimits, timeoutInMs = constant_1.defaultTimeoutInMs) {
    const res = await (0, requestUtils_1.sendRequestWithRetry)(async () => {
        return await axios_1.default.get(url, {
            responseType: "arraybuffer",
            timeout: timeoutInMs,
        });
    }, tryLimits);
    const zip = new adm_zip_1.default(res.data);
    return zip;
}
/* The unzip used for scaffold which would drop the attr of the files and dirs. */
async function unzip(zip, dstPath, nameReplaceFn, dataReplaceFn, filterFn) {
    const output = [];
    let entries = zip.getEntries().filter((entry) => !entry.isDirectory);
    if (filterFn) {
        entries = entries.filter((entry) => filterFn(entry.entryName));
    }
    for (const entry of entries) {
        const rawEntryData = entry.getData();
        const entryName = nameReplaceFn
            ? nameReplaceFn(entry.entryName, rawEntryData)
            : entry.entryName;
        const entryData = dataReplaceFn
            ? dataReplaceFn(entry.name, rawEntryData)
            : rawEntryData;
        const filePath = path_1.default.join(dstPath, entryName);
        const dirPath = path_1.default.dirname(filePath);
        await fs.ensureDir(dirPath);
        await fs.writeFile(filePath, entryData);
        output.push(entryName);
    }
    return output;
}
function renderTemplateFileData(fileName, fileData, variables) {
    //only mustache files with name ending with .tpl
    if (path_1.default.extname(fileName) === constant_1.templateFileExt) {
        const token = escapeEmptyVariable(fileData.toString(), variables ?? {});
        const writer = new mustache_1.Writer();
        const result = writer.renderTokens(token, new mustache_1.Context(variables));
        // Be compatible with current stable templates, can be removed after new template released.
        // Disable HTML escaping to prevent URLs and other content from being encoded
        mustache_1.default.escape = (value) => value;
        return mustache_1.default.render(result, variables, {}, constant_1.oldPlaceholderDelimiters);
    }
    // Return Buffer instead of string if the file is not a template. Because `toString()` may break binary resources, like png files.
    return fileData;
}
function escapeEmptyVariable(template, view, tags = constant_1.placeholderDelimiters) {
    const parsed = mustache_1.default.parse(template, tags);
    const tokens = (0, lodash_1.cloneDeep)(parsed); // Mustache cache the parsed result. Modify the result in place may cause unexpected issue.
    updateTokens(tokens, view, tags, 0);
    return tokens;
}
function updateTokens(tokens, view, tags, accShift) {
    const shift = tags[0].length + tags[1].length;
    // token: [Type, Value, Start, End]
    for (const token of tokens) {
        token[2] += accShift;
        const value = token[1];
        if (token[0] === "name" && (view[value] === undefined || view[value] === null)) {
            token[0] = "text";
            token[1] = tags[0] + value + tags[1];
            accShift += shift;
        }
        else if (token[0] === "#" || token[0] === "^") {
            token[2] += accShift;
            token[3] += accShift;
            let rootView = view;
            if (Array.isArray(view[token[1]])) {
                rootView = { ...view, ...view[token[1]][0] };
            }
            else if (typeof view[token[1]] === "object") {
                rootView = { ...view, ...view[token[1]] };
            }
            accShift += updateTokens(token[4], rootView, tags, accShift);
            token[5] += accShift;
        }
    }
    return accShift;
}
function renderTemplateFileName(fileName, fileData, variables) {
    // Disable HTML escaping to prevent special characters from being encoded
    mustache_1.default.escape = (value) => value;
    return mustache_1.default.render(fileName, variables, {}, constant_1.placeholderDelimiters).replace(constant_1.templateFileExt, "");
}
async function getSampleInfoFromName(sampleName) {
    const sample = (await samples_1.sampleProvider.SampleCollection).samples.find((sample) => sample.id.toLowerCase() === sampleName.toLowerCase());
    if (!sample) {
        throw new error_1.InputValidationError(`sample '${sampleName}'`, "not found");
    }
    return sample;
}
function zipFolder(folderPath) {
    const zip = new adm_zip_1.default();
    zip.addLocalFolder(folderPath);
    return zip;
}
async function downloadDirectory(sampleInfo, dstPath, concurrencyLimits = constant_1.sampleConcurrencyLimits, retryLimits = constant_1.sampleDefaultRetryLimits) {
    const { samplePaths, fileUrlPrefix } = await getSampleFileInfo(sampleInfo, retryLimits);
    await downloadSampleFiles(sampleInfo, fileUrlPrefix, samplePaths, dstPath, sampleInfo.dir, retryLimits, concurrencyLimits);
    return samplePaths;
}
async function getSampleFileInfo(urlInfo, retryLimits) {
    const fileInfoUrl = `https://api.github.com/repos/${urlInfo.owner}/${urlInfo.repository}/git/trees/${urlInfo.ref}?recursive=1`;
    const fileInfo = (await (0, requestUtils_1.sendRequestWithRetry)(async () => {
        return await axios_1.default.get(fileInfoUrl);
    }, retryLimits)).data;
    const samplePaths = fileInfo?.tree
        ?.filter((node) => node.path.startsWith(`${urlInfo.dir}/`) && node.type !== "tree")
        .map((node) => node.path);
    const fileUrlPrefix = `https://raw.githubusercontent.com/${urlInfo.owner}/${urlInfo.repository}/${fileInfo?.sha}/`;
    return { samplePaths, fileUrlPrefix };
}
async function downloadSampleFiles(sampleInfo, fileUrlPrefix, samplePaths, dstPath, relativePath, retryLimits, concurrencyLimits) {
    const downloadCallback = async (samplePath) => {
        const lfsRegex = /^.*oid sha256:[0-9a-f]+\nsize \d+/gm;
        const file = (await (0, requestUtils_1.sendRequestWithRetry)(async () => {
            const content = await axios_1.default.get(fileUrlPrefix + samplePath, { responseType: "arraybuffer" });
            if (lfsRegex.test(content.data.toString())) {
                return await axios_1.default.get(`https://media.githubusercontent.com/media/${sampleInfo.owner}/${sampleInfo.repository}/${sampleInfo.ref}/${samplePath}`, {
                    responseType: "arraybuffer",
                });
            }
            else {
                return content;
            }
        }, retryLimits));
        const filePath = path_1.default.join(dstPath, path_1.default.relative(`${relativePath}/`, samplePath));
        await fs.ensureFile(filePath);
        await fs.writeFile(filePath, Buffer.from(file.data));
    };
    await runWithLimitedConcurrency(samplePaths, downloadCallback, concurrencyLimits);
}
async function runWithLimitedConcurrency(items, callback, concurrencyLimit) {
    const queue = [];
    for (const item of items) {
        // fire the async function, add its promise to the queue, and remove
        // it from queue when complete
        const p = callback(item)
            .then((res) => {
            queue.splice(queue.indexOf(p), 1);
            return res;
        })
            .catch((err) => {
            throw err;
        });
        queue.push(p);
        // if max concurrent, wait for one to finish
        if (queue.length >= concurrencyLimit) {
            await Promise.race(queue);
        }
    }
    // wait for the rest of the calls to finish
    await Promise.all(queue);
}
function convertToLangKey(programmingLanguage) {
    switch (programmingLanguage) {
        case constants_2.ProgrammingLanguage.JS: {
            return "js";
        }
        case constants_2.ProgrammingLanguage.TS: {
            return "ts";
        }
        case constants_2.ProgrammingLanguage.CSharp: {
            return "csharp";
        }
        case constants_2.ProgrammingLanguage.PY: {
            return "python";
        }
        case constants_2.ProgrammingLanguage.None: {
            return "common";
        }
    }
    return programmingLanguage;
}
function convertToUrl(sampleInfo) {
    return `https://github.com/${sampleInfo.owner}/${sampleInfo.repository}/tree/${sampleInfo.ref}/${sampleInfo.dir}`;
}
function simplifyAxiosError(error) {
    const simplifiedError = {
        message: error.message,
        name: error.name,
        config: error.config,
        code: error.code,
        stack: error.stack,
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data,
    };
    return simplifiedError;
}
function isApiLimitError(error) {
    //https://docs.github.com/en/rest/overview/rate-limits-for-the-rest-api?apiVersion=2022-11-28#exceeding-the-rate-limit
    return (axios_1.default.isAxiosError(error) &&
        error.response?.status !== undefined &&
        [403, 429].includes(error.response.status) &&
        error.response?.headers?.["x-ratelimit-remaining"] === "0");
}
async function setGeneralSensitivityLabel(context, declarativeAgentManifestPath) {
    try {
        const loginStatusRes = await context.tokenProvider?.m365TokenProvider?.getStatus({
            scopes: [constants_1.ListSensitivityLabelScope],
        });
        if (!loginStatusRes || loginStatusRes.isErr()) {
            context.logProvider?.info((0, localizeUtils_1.getDefaultString)("error.listSensitivityLabel.tokenFailed", loginStatusRes?.error.message));
            return;
        }
        if (loginStatusRes.value.status != teamsfx_api_1.signedIn) {
            context.logProvider?.info((0, localizeUtils_1.getDefaultString)("core.listSensitivityLabel.notLogin"));
            return;
        }
        if (loginStatusRes.value.token == undefined) {
            context.logProvider?.info((0, localizeUtils_1.getDefaultString)("error.listSensitivityLabel.tokenUndefined"));
            return;
        }
        const graphClient = new graphClient_1.GraphClient(context.tokenProvider.m365TokenProvider);
        const result = await graphClient.getGeneralSentivityLabel(loginStatusRes.value.token);
        if (result.isErr()) {
            throw result.error;
        }
        const generalLabel = result.value;
        const declarativeAgentManifestRes = await CopilotGptManifestUtils_1.copilotGptManifestUtils.readDeclarativeAgentManifestFile(declarativeAgentManifestPath);
        if (declarativeAgentManifestRes.isErr()) {
            context.logProvider?.info((0, localizeUtils_1.getDefaultString)("error.readDeclarativeAgentManifest.failed", declarativeAgentManifestRes.error));
            return;
        }
        const declarativeAgentManifest = declarativeAgentManifestRes.value;
        declarativeAgentManifest.sensitivity_label = {
            id: generalLabel.id,
        };
        const writeRes = await CopilotGptManifestUtils_1.copilotGptManifestUtils.writeDeclarativeAgentManifestFile(declarativeAgentManifest, declarativeAgentManifestPath);
        if (writeRes.isErr()) {
            context.logProvider?.info((0, localizeUtils_1.getDefaultString)("error.writeDeclarativeAgentManifest.failed", writeRes.error));
            return;
        }
    }
    catch (error) {
        context.logProvider?.info((0, localizeUtils_1.getDefaultString)("error.setGeneralSensitivityLabel.failed", error.message));
    }
}
//# sourceMappingURL=utils.js.map